/**
 * MODULE: BrainLearningsNavBadge.tsx
 * PURPOSE: Compact pink/violet learnings counter in the app top bar → Settings learned preferences.
 * RECENT: 2026-06-06 [2.2.1330] — Top-nav badge for active learned preferences; links **`brainLearnedPreferencesSettingsPath()`**.
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 48 / 200 lines
 */

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Brain } from 'lucide-react';
import { brainLearnedPreferencesSettingsPath } from '@shared/lib/frontend/utils/brainInsightLinks';
import { BRAZN_BRAIN_PREFERENCES_CHANGED } from '@shared/lib/frontend/utils/brainPreferenceEvents';
import { useAssistantContext } from '../../hooks/assistant/useAssistantContext';
import { brainLearningsNavBadgeCount } from './brainLearningsNavCount';

export function BrainLearningsNavBadge() {
  const { context, refresh } = useAssistantContext({
    enabled: true,
    debounceMs: 800,
    followUpRefreshDelaysMs: [5000, 15000],
  });

  useEffect(() => {
    const onRefresh = () => refresh();
    window.addEventListener(BRAZN_BRAIN_PREFERENCES_CHANGED, onRefresh);
    return () => window.removeEventListener(BRAZN_BRAIN_PREFERENCES_CHANGED, onRefresh);
  }, [refresh]);

  const count = brainLearningsNavBadgeCount(context);
  if (count <= 0) return null;

  const label = count === 1 ? '1 active learning' : `${count} active learnings`;

  return (
    <Link
      to={brainLearnedPreferencesSettingsPath()}
      className="inline-flex items-center gap-1.5 rounded-full border border-violet-300/80 bg-gradient-to-r from-violet-100 to-fuchsia-100 px-2.5 py-1 text-xs font-semibold text-violet-900 shadow-sm transition hover:from-violet-200 hover:to-fuchsia-200 dark:border-violet-700/60 dark:from-violet-950/70 dark:to-fuchsia-950/50 dark:text-violet-100 dark:hover:from-violet-900/80 dark:hover:to-fuchsia-900/60"
      aria-label={`${label} — open learnings in Settings`}
      title={label}
      data-testid="topbar-brain-learnings-badge"
    >
      <Brain className="h-3.5 w-3.5 shrink-0 text-violet-600 dark:text-violet-300" aria-hidden />
      <span className="tabular-nums">{count > 99 ? '99+' : count}</span>
    </Link>
  );
}
